import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Button, Alert,
  Grid, Card, CardContent, Divider, Stack, CircularProgress,
  List, ListItem, ListItemText, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PasswordDialog from './PasswordDialog';
import ConfirmDialog  from './ConfirmDialog';
import { getStatistics, exportQuarterly, exportEvent, getEvents, getGroupSettings } from '../api';

function Dashboard({ activeGroup, groupPassword, refreshTrigger }) {
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [exporting, setExporting]     = useState(false);
  const [message, setMessage]         = useState(null);
  const [bankDetails, setBankDetails] = useState(null);

  // View: 'regular' (quarterly declaration) or an event id
  const [events, setEvents] = useState([]);
  const [view, setView]     = useState('regular');
  const isEvent = view !== 'regular';

  // Dialog state
  const [pwdDialog, setPwdDialog]         = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);

  // Scope: 'outstanding' (confirmed only) or 'all' (all-time) — regular view only
  const [scope, setScope] = useState('outstanding');

  // Reset to the regular view when switching groups (event ids are group-specific)
  useEffect(() => { setView('regular'); }, [activeGroup]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); }, [refreshTrigger, activeGroup, view]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBankDetails(); loadEvents(); }, [activeGroup]);

  const loadStats = async () => {
    setLoading(true);
    try   { setStats(await getStatistics(activeGroup, isEvent ? view : null)); }
    catch { setStats(null); }
    finally { setLoading(false); }
  };

  const loadEvents = async () => {
    try   { setEvents(await getEvents(activeGroup)); }
    catch { /* non-critical */ }
  };

  const loadBankDetails = async () => {
    try   { setBankDetails(await getGroupSettings(activeGroup)); }
    catch { /* non-critical */ }
  };

  const bankDetailsMissing =
    !bankDetails ||
    !bankDetails.naam_rekeninghouder?.trim() ||
    !bankDetails.iban_rekeningnummer?.trim();

  // ── Regular (quarterly) export: password → confirm → download ──
  const handleExportClick = () => {
    if (bankDetailsMissing) {
      setMessage({ type: 'error', text: 'Vul eerst de bankgegevens in via "Stafleden Beheren" → Bankgegevens Penningmeester, anders blijft het exportformulier leeg.' });
      return;
    }
    setPwdDialog(true);
  };

  const handlePasswordConfirm = (password) => {
    setPwdDialog(false);
    if (password !== groupPassword) { setMessage({ type: 'error', text: 'Onjuist wachtwoord!' }); return; }
    if (isEvent) { runEventExport(password); return; }
    setConfirmDialog(true);
  };

  const handleExportConfirm = async () => {
    setConfirmDialog(false);
    setExporting(true);
    setMessage(null);
    try {
      await exportQuarterly(activeGroup);
      setMessage({ type: 'success', text: 'Export succesvol! Het ZIP-bestand is gedownload.' });
      loadStats();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fout bij exporteren: ' + err.message });
    } finally { setExporting(false); }
  };

  // ── Event export & close: exports the ZIP and closes the event in one step ──
  const runEventExport = async (password) => {
    setExporting(true);
    setMessage(null);
    try {
      await exportEvent(view, password);
      // The event is now closed and drops out of the (active-only) selector.
      setView('regular');
      setMessage({ type: 'success', text: 'Geëxporteerd en afgesloten! Het ZIP-bestand is gedownload.' });
      loadEvents();
    } catch (err) {
      let detail;
      try {
        if (err.response?.data instanceof Blob) detail = JSON.parse(await err.response.data.text())?.detail;
        else detail = err.response?.data?.detail;
      } catch { /* ignore */ }
      setMessage({ type: 'error', text: 'Fout bij exporteren: ' + (detail || err.message) });
    } finally { setExporting(false); }
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

  const renderOverviews = (memberTotals, categoryTotals, categoryLabel) => (
    <>
      <Typography variant="h6" fontWeight={600} mb={1}>Overzicht per Staflid</Typography>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 1.5 }}>
        <List disablePadding>
          {memberTotals.map((m, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Divider />}
              <ListItem>
                <ListItemText primary={m.name} />
                <Typography fontWeight={700} color="primary">{formatCurrency(m.total)}</Typography>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Typography variant="h6" fontWeight={600} mb={1}>{categoryLabel}</Typography>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 1.5 }}>
        <List disablePadding>
          {categoryTotals.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Divider />}
              <ListItem>
                <ListItemText primary={c.category} />
                <Typography fontWeight={700} color="primary">{formatCurrency(c.total)}</Typography>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </>
  );

  const StatCard = ({ label, value }) => (
    <Grid item xs={6} key={label}>
      <Card sx={{ background: 'linear-gradient(135deg, var(--stat-from), var(--stat-to))', '& *': { color: 'white' } }}
        style={{ '--stat-from': 'currentColor', '--stat-to': 'currentColor' }}
      >
        <CardContent sx={{ bgcolor: 'primary.main', borderRadius: 'inherit', '&:last-child': { pb: 2 } }}>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>{label}</Typography>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) return <Paper elevation={1} sx={{ borderRadius: 2, p: 3, textAlign: 'center' }}><CircularProgress /></Paper>;

  if (!stats) return (
    <Paper elevation={1} sx={{ borderRadius: 2, p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Dashboard & Export</Typography>
      <Alert severity="error">Kan statistieken niet laden. Controleer of de backend bereikbaar is.</Alert>
    </Paper>
  );

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Dashboard & Export</Typography>

      {message && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* View selector: regular declaration or a specific event */}
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <InputLabel>Weergave</InputLabel>
        <Select value={view} label="Weergave" onChange={e => setView(e.target.value)}>
          <MenuItem value="regular">📋 Reguliere declaraties</MenuItem>
          {events.filter(ev => ev.status === 'active').map(ev => (
            <MenuItem key={ev.id} value={ev.id}>🎯 {ev.title}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {!isEvent ? (
        /* ── Regular (quarterly) view ── */
        <>
          <Tabs
            value={scope}
            onChange={(_, v) => setScope(v)}
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="outstanding" label="Openstaand" />
            <Tab value="all" label="Totaal" />
          </Tabs>

          {(() => {
            const isAll = scope === 'all';
            const receipts     = isAll ? (stats.all_time_receipts ?? stats.total_receipts) : stats.total_receipts;
            const amount       = isAll ? (stats.all_time_amount ?? stats.total_amount) : stats.total_amount;
            const memberTotals   = isAll ? (stats.all_time_member_totals ?? stats.member_totals) : stats.member_totals;
            const categoryTotals = isAll ? (stats.all_time_category_totals ?? stats.category_totals) : stats.category_totals;

            return (
              <>
                <Grid container spacing={2} mb={3}>
                  <StatCard label={isAll ? 'Totaal Bonnetjes' : 'Openstaande Bonnetjes'} value={receipts} />
                  <StatCard label={isAll ? 'Totaal Bedrag'     : 'Openstaand Bedrag'}     value={formatCurrency(amount)} />
                </Grid>
                {renderOverviews(memberTotals, categoryTotals, 'Overzicht per Categorie')}
              </>
            );
          })()}

          {/* Regular export section */}
          <Paper variant="outlined" sx={(theme) => ({ p: 3, borderRadius: 1.5, bgcolor: theme.palette.grey[50] })}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>Reguliere Declaratie Exporteren</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Genereer een ZIP-bestand met alle bonnetjes en een ingevuld Excel declaratieformulier voor {activeGroup}.
            </Typography>

            {bankDetailsMissing && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>⚠️ Bankgegevens ontbreken</strong><br />
                Ga naar <strong>Stafleden Beheren → Bankgegevens Penningmeester</strong> en vul de naam rekeninghouder en het IBAN in voordat je exporteert.
              </Alert>
            )}

            <Stack alignItems="center" spacing={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleExportClick}
                disabled={exporting || stats.total_receipts === 0}
              >
                {exporting ? 'Bezig met exporteren...' : 'Exporteer Declaratie'}
              </Button>
              {stats.total_receipts === 0 && (
                <Typography variant="caption" color="text.disabled">Er zijn geen bonnetjes om te exporteren</Typography>
              )}
            </Stack>
          </Paper>
        </>
      ) : (
        /* ── Event view ── */
        <>
          <Grid container spacing={2} mb={3}>
            <StatCard label="Voorschot"  value={formatCurrency(stats.event?.voorschot_ontvangen ?? 0)} />
            <StatCard label="Uitgegeven" value={formatCurrency(stats.event?.total_spent ?? stats.all_time_amount)} />
            <StatCard label="Balans"     value={formatCurrency(stats.event?.balance ?? 0)} />
            <StatCard label="Bonnetjes"  value={stats.all_time_receipts} />
          </Grid>

          {renderOverviews(stats.all_time_member_totals, stats.all_time_category_totals, 'Overzicht per Kostenpost')}

          {/* Event export section */}
          <Paper variant="outlined" sx={(theme) => ({ p: 3, borderRadius: 1.5, bgcolor: theme.palette.grey[50] })}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>Evenement Exporteren & Afsluiten</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Genereer een ZIP-bestand met alle bonnetjes en een ingevuld Excel declaratieformulier voor {stats.event?.title || 'dit evenement'} en sluit het evenement af. Je kunt het later heropenen via het tabblad Evenementen.
            </Typography>

            <Stack alignItems="center" spacing={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={() => setPwdDialog(true)}
                disabled={exporting || stats.all_time_receipts === 0}
              >
                {exporting ? 'Bezig met exporteren...' : 'Exporteer & Afsluiten'}
              </Button>
              {stats.all_time_receipts === 0 && (
                <Typography variant="caption" color="text.disabled">Dit evenement heeft geen bonnetjes</Typography>
              )}
            </Stack>
          </Paper>
        </>
      )}

      <PasswordDialog
        open={pwdDialog}
        onClose={() => setPwdDialog(false)}
        onConfirm={handlePasswordConfirm}
        title="Export bevestigen"
        description="Voer het wachtwoord in om de declaratie te exporteren."
      />

      <ConfirmDialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        onConfirm={handleExportConfirm}
        title="Declaratie exporteren?"
        message="Dit markeert alle bevestigde bonnetjes als geëxporteerd. Wil je doorgaan?"
        confirmText="Exporteren"
        confirmColor="primary"
      />
    </Paper>
  );
}

export default Dashboard;
